import React, { useState } from 'react';
import { Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { formatCurrency } from '../utils/calculations';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import SEO from '../components/SEO';

ChartJS.register(ArcElement, Tooltip, Legend);

const RetirementCalculator = () => {
  const [currentAge, setCurrentAge] = useState(30);
  const [retirementAge, setRetirementAge] = useState(60);
  const [lifeExpectancy, setLifeExpectancy] = useState(85);
  const [monthlyExpense, setMonthlyExpense] = useState(50000);
  const [currentSavings, setCurrentSavings] = useState(500000);
  const [expectedReturn, setExpectedReturn] = useState(12);
  const [postRetirementReturn, setPostRetirementReturn] = useState(7);
  const [inflation, setInflation] = useState(6);
  const [result, setResult] = useState(null);

  const calculate = () => {
    const cAge = Number(currentAge) || 0;
    const rAge = Number(retirementAge) || 0;
    const lExp = Number(lifeExpectancy) || 0;
    const mExp = Number(monthlyExpense) || 0;
    const cSavings = Number(currentSavings) || 0;
    const expRet = Number(expectedReturn) || 0;
    const postRet = Number(postRetirementReturn) || 0;
    const inf = Number(inflation) || 0;
    
    const yearsToRetirement = rAge - cAge;
    const yearsAfterRetirement = lExp - rAge;
    
    // Inflation-adjusted monthly expense at retirement
    const futureMonthlyExpense = mExp * Math.pow(1 + inf / 100, yearsToRetirement);
    const futureAnnualExpense = futureMonthlyExpense * 12;
    
    // Calculate corpus needed at retirement (present value of all future expenses)
    const realReturnPostRetirement = ((1 + postRet / 100) / (1 + inf / 100)) - 1;
    let corpusNeeded;
    
    if (realReturnPostRetirement > 0) {
      corpusNeeded = futureAnnualExpense * (1 - Math.pow(1 + realReturnPostRetirement, -yearsAfterRetirement)) / realReturnPostRetirement;
    } else {
      corpusNeeded = futureAnnualExpense * yearsAfterRetirement;
    }
    
    // Future value of current savings
    const futureSavings = cSavings * Math.pow(1 + expRet / 100, yearsToRetirement);
    
    // Additional corpus needed
    const additionalCorpusNeeded = Math.max(0, corpusNeeded - futureSavings);
    
    // Monthly SIP required
    const monthlyReturn = expRet / 12 / 100;
    const months = yearsToRetirement * 12;
    let monthlySIPRequired = 0;
    
    if (additionalCorpusNeeded > 0) {
      monthlySIPRequired = additionalCorpusNeeded * monthlyReturn / (Math.pow(1 + monthlyReturn, months) - 1);
    }
    
    // Total investment
    const totalInvestment = monthlySIPRequired * months + cSavings;
    const wealthGained = corpusNeeded - totalInvestment;

    setResult({
      yearsToRetirement,
      yearsAfterRetirement,
      currentMonthlyExpense: mExp,
      futureMonthlyExpense,
      futureAnnualExpense,
      corpusNeeded,
      futureSavings,
      additionalCorpusNeeded,
      monthlySIPRequired,
      totalInvestment,
      wealthGained,
      monthlyPension: futureMonthlyExpense,
    });
  };

  const formatCurrencyForPDF = (amount) => 'Rs. ' + Math.round(amount).toLocaleString('en-IN');

  const downloadPDF = () => {
    if (!result) return;
    const doc = new jsPDF();
    
    doc.setFontSize(18);
    doc.text('Retirement Planning Report', 105, 20, { align: 'center' });
    
    doc.setFontSize(10);
    doc.text(`Generated on: ${new Date().toLocaleDateString('en-IN')}`, 105, 28, { align: 'center' });

    autoTable(doc, {
      startY: 40,
      head: [['Input Parameter', 'Value']],
      body: [
        ['Current Age', `${currentAge} years`],
        ['Retirement Age', `${retirementAge} years`],
        ['Life Expectancy', `${lifeExpectancy} years`],
        ['Current Monthly Expense', formatCurrencyForPDF(monthlyExpense)],
        ['Current Savings', formatCurrencyForPDF(currentSavings)],
        ['Expected Return (Pre-Retirement)', `${expectedReturn}%`],
        ['Expected Return (Post-Retirement)', `${postRetirementReturn}%`],
        ['Inflation Rate', `${inflation}%`],
      ],
      theme: 'striped',
      headStyles: { fillColor: [37, 99, 235] },
    });

    let finalY = doc.lastAutoTable.finalY + 10;
    doc.text('Retirement Analysis', 14, finalY);

    autoTable(doc, {
      startY: finalY + 5,
      head: [['Description', 'Amount']],
      body: [
        ['Years to Retirement', `${result.yearsToRetirement} years`],
        ['Retirement Duration', `${result.yearsAfterRetirement} years`],
        ['Monthly Expense at Retirement', formatCurrencyForPDF(result.futureMonthlyExpense)],
        ['Total Corpus Required', formatCurrencyForPDF(result.corpusNeeded)],
        ['Future Value of Current Savings', formatCurrencyForPDF(result.futureSavings)],
        ['Additional Corpus Needed', formatCurrencyForPDF(result.additionalCorpusNeeded)],
        ['Monthly SIP Required', formatCurrencyForPDF(result.monthlySIPRequired)],
      ],
      theme: 'striped',
      headStyles: { fillColor: [16, 185, 129] },
    });

    doc.save('Retirement_Plan.pdf');
  };

  const chartData = result ? {
    labels: ['Current Savings (Future Value)', 'SIP Investment Needed', 'Wealth Gained'],
    datasets: [{
      data: [result.futureSavings, result.additionalCorpusNeeded > 0 ? result.monthlySIPRequired * result.yearsToRetirement * 12 : 0, result.wealthGained],
      backgroundColor: ['#3b82f6', '#f59e0b', '#10b981'],
      borderWidth: 0,
    }]
  } : null;

  return (
    <div className="calculator-page">
      <SEO 
        title="Retirement Calculator"
        description="Free Retirement Calculator India - Plan retirement corpus. Calculate monthly savings needed for retirement."
        keywords="retirement calculator, retirement planning calculator, retirement corpus calculator, retirement savings calculator India"
        canonicalPath="/retirement"
      />
      <div className="calculator-header">
        <h1>🏖️ Retirement Planning Calculator</h1>
        <p>Plan your retirement corpus and monthly investment needed</p>
      </div>

      <div className="calculator-container">
        <div className="calculator-inputs">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
            <div className="input-group">
              <label>Current Age</label>
              <input type="number" value={currentAge === '' ? '' : currentAge} onChange={(e) => setCurrentAge(e.target.value === '' ? '' : Number(e.target.value))} />
            </div>
            <div className="input-group">
              <label>Retirement Age</label>
              <input type="number" value={retirementAge === '' ? '' : retirementAge} onChange={(e) => setRetirementAge(e.target.value === '' ? '' : Number(e.target.value))} />
            </div>
            <div className="input-group">
              <label>Life Expectancy</label>
              <input type="number" value={lifeExpectancy === '' ? '' : lifeExpectancy} onChange={(e) => setLifeExpectancy(e.target.value === '' ? '' : Number(e.target.value))} />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div className="input-group">
              <label>Current Monthly Expense (₹)</label>
              <input type="number" value={monthlyExpense === '' ? '' : monthlyExpense} onChange={(e) => setMonthlyExpense(e.target.value === '' ? '' : Number(e.target.value))} />
            </div>
            <div className="input-group">
              <label>Current Savings (₹)</label>
              <input type="number" value={currentSavings === '' ? '' : currentSavings} onChange={(e) => setCurrentSavings(e.target.value === '' ? '' : Number(e.target.value))} />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
            <div className="input-group">
              <label>Pre-Retirement Return (%)</label>
              <input type="number" step="0.5" value={expectedReturn === '' ? '' : expectedReturn} onChange={(e) => setExpectedReturn(e.target.value === '' ? '' : Number(e.target.value))} />
            </div>
            <div className="input-group">
              <label>Post-Retirement Return (%)</label>
              <input type="number" step="0.5" value={postRetirementReturn === '' ? '' : postRetirementReturn} onChange={(e) => setPostRetirementReturn(e.target.value === '' ? '' : Number(e.target.value))} />
            </div>
            <div className="input-group">
              <label>Inflation Rate (%)</label>
              <input type="number" step="0.5" value={inflation === '' ? '' : inflation} onChange={(e) => setInflation(e.target.value === '' ? '' : Number(e.target.value))} />
            </div>
          </div>

          <button className="btn btn-primary" style={{ width: '100%', marginTop: '10px' }} onClick={calculate}>
            Calculate Retirement Plan
          </button>
        </div>

        <div className="calculator-results">
          {result ? (
            <>
              <div className="result-summary" style={{ background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)' }}>
                <h3>Corpus Required at Retirement</h3>
                <div className="amount">{formatCurrency(result.corpusNeeded)}</div>
                <p style={{ opacity: 0.9, marginTop: '8px' }}>
                  Monthly SIP Needed: {formatCurrency(result.monthlySIPRequired)}
                </p>
              </div>

              <div className="result-details">
                <div className="result-item">
                  <span className="label">Years to Retirement</span>
                  <span className="value">{result.yearsToRetirement} years</span>
                </div>
                <div className="result-item">
                  <span className="label">Retirement Duration</span>
                  <span className="value">{result.yearsAfterRetirement} years</span>
                </div>
                <div className="result-item">
                  <span className="label">Current Monthly Expense</span>
                  <span className="value">{formatCurrency(result.currentMonthlyExpense)}</span>
                </div>
                <div className="result-item" style={{ background: '#fef3c7' }}>
                  <span className="label">Monthly Expense at Retirement</span>
                  <span className="value" style={{ color: '#f59e0b' }}>{formatCurrency(result.futureMonthlyExpense)}</span>
                </div>
                <div className="result-item">
                  <span className="label">Your Current Savings Will Grow To</span>
                  <span className="value">{formatCurrency(result.futureSavings)}</span>
                </div>
                <div className="result-item">
                  <span className="label">Additional Corpus Needed</span>
                  <span className="value">{formatCurrency(result.additionalCorpusNeeded)}</span>
                </div>
                <div className="result-item" style={{ background: '#ecfdf5' }}>
                  <span className="label">Monthly SIP Required</span>
                  <span className="value" style={{ color: '#10b981', fontWeight: '600' }}>{formatCurrency(result.monthlySIPRequired)}</span>
                </div>
              </div>

              {chartData && (
                <div className="chart-container">
                  <h4>Corpus Breakdown</h4>
                  <Pie data={chartData} options={{ responsive: true, plugins: { legend: { position: 'bottom' } } }} />
                </div>
              )}

              <button className="btn btn-secondary" style={{ width: '100%', marginTop: '20px' }} onClick={downloadPDF}>
                Download Retirement Plan PDF
              </button>
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: '#64748b' }}>
              <p>Enter your details to get a personalized retirement plan</p>
            </div>
          )}
        </div>
      </div>

      <div className="info-box">
        <h4>💡 Retirement Planning Tips</h4>
        <ul>
          <li>Start early - the power of compounding works best over longer periods</li>
          <li>Account for healthcare costs which increase significantly after 60</li>
          <li>Consider NPS for additional tax benefits under 80CCD(1B)</li>
          <li>Maintain emergency fund of 12-24 months expenses before retirement</li>
        </ul>
      </div>
    </div>
  );
};

export default RetirementCalculator;
